import logger from '@/lib/logger';
import { apiClient } from '@/lib/api';

export interface TestByDay {
  date: string; // ISO 8601 date (YYYY-MM-DD)
  count: number;
  sampled: number;
  failed: number;
  cancelled: number;
}

export interface PathogenClassificationResult {
  pathogenName: string;
  channelId: number;
  status: 'positive' | 'negative' | 'inconclusive' | null;
  confidence: number;
  concentration?: number;
}

export interface RecentTest {
  id: string;
  sampleCode?: string;
  profileName?: string;  // Measurement Profile Name
  patientName?: string;
  status: string;
  startedAt: string; // Always present (falls back to createdAt)
  endedAt: string | null;
  pathogenClassificationResults?: PathogenClassificationResult[];
}

export interface DashboardSummary {
  totalTests: number;
  testsToday: number;
  testsThisWeek: number;
  testsThisMonth: number;
  activeTests: number;
  failedTestsLast30Days: number;
  testsByDay: TestByDay[];
  recentTests: RecentTest[];
}

export interface DashboardParams {
  labId?: string;
  days?: number;
}

// TODO (MOCK): Moved development-only mock helpers into `./mockData.ts`.
// Remove this import and the mock fallback in `getSummary` once the production
// backend is stable and tests use explicit fixtures.
import { generateMockDashboardData } from './mockData';

export const dashboardService = {
  /**
   * Get dashboard summary data
   * GET /api/v1/dashboard/summary
   */
  async getSummary(params?: DashboardParams): Promise<DashboardSummary> {
    try {
      const response = await apiClient.get<DashboardSummary>('/api/v1/dashboard/summary', {
        params: {
          days: params?.days || 30,
          labId: params?.labId,
        },
      });

      // Backwards-compatibility shim: some backend builds return `testsPerDay`.
      const data = (response.data as unknown) as any;
      if (!data.testsByDay && data.testsPerDay) {
        data.testsByDay = data.testsPerDay;
      }

      return data as DashboardSummary;
    } catch (error) {
      // TODO (MOCK): Use `VITE_USE_MOCK_DATA` to control fallback behavior.
      // If disabled, rethrow the error so callers can surface failures.
      const useMock = (import.meta.env.VITE_USE_MOCK_DATA === 'true') || import.meta.env.DEV;
      if (!useMock) {
        throw error;
      }

      logger.warn('[DashboardService] API not available, using mock data');
      return generateMockDashboardData();
    }
  },
};
